import React from 'react';
import { ExternalLink, Phone, MessageCircle, Image as ImageIcon, FileText, Video } from 'lucide-react';

function fillPlaceholders(text, examples = []) {
  if (!text) return '';
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const i = parseInt(n, 10) - 1;
    return examples[i] != null && examples[i] !== '' ? examples[i] : `{{${n}}}`;
  });
}

function MediaHeaderPlaceholder({ type, label }) {
  const Icon = type === 'IMAGE' ? ImageIcon : type === 'VIDEO' ? Video : FileText;
  return (
    <div className="bg-[#f0f2f5] h-32 flex flex-col items-center justify-center text-gray-400 gap-1.5 border-b border-gray-100">
      <Icon size={22} strokeWidth={1.5} />
      <span className="text-[11px] font-medium">{label || type}</span>
    </div>
  );
}

function PreviewButton({ btn }) {
  const text = btn?.text || 'Button';
  if (btn?.type === 'URL') {
    const content = (
      <>
        <ExternalLink size={14} />
        <span className="font-medium">{text}</span>
      </>
    );
    if (btn.url && /^https?:\/\//i.test(btn.url)) {
      return (
        <a
          href={btn.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 text-[#008069] text-[13px] py-2.5 border-t border-gray-100/80 hover:bg-black/5"
        >
          {content}
        </a>
      );
    }
    return (
      <div className="flex items-center justify-center gap-1.5 text-[#008069] text-[13px] py-2.5 border-t border-gray-100/80">
        {content}
      </div>
    );
  }
  if (btn?.type === 'PHONE_NUMBER') {
    const phone = btn.phone_number || '';
    const inner = (
      <>
        <Phone size={14} />
        <span className="font-medium">{text}</span>
      </>
    );
    if (phone) {
      return (
        <a
          href={`tel:${phone}`}
          className="flex items-center justify-center gap-1.5 text-[#008069] text-[13px] py-2.5 border-t border-gray-100/80 hover:bg-black/5"
        >
          {inner}
        </a>
      );
    }
    return (
      <div className="flex items-center justify-center gap-1.5 text-[#008069] text-[13px] py-2.5 border-t border-gray-100/80">
        {inner}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center gap-1.5 text-[#008069] text-[13px] py-2.5 border-t border-gray-100/80">
      <MessageCircle size={14} />
      <span className="font-medium">{text}</span>
    </div>
  );
}

/** Buttons shown inside chat bubbles for sent template messages. */
export function TemplateBubbleButtons({ buttons = [] }) {
  if (!buttons.length) return null;
  const ctaButtons = buttons.filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
  const quickReplies = buttons.filter((b) => b.type === 'QUICK_REPLY');

  return (
    <>
      {ctaButtons.map((btn, i) => (
        <PreviewButton key={`cta-${i}`} btn={btn} />
      ))}
      {quickReplies.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2 pb-2 pt-1 border-t border-gray-100/80">
          {quickReplies.map((btn, i) => (
            <span
              key={`qr-${i}`}
              className="text-[12px] bg-white/80 border border-gray-200 rounded-full px-3 py-1 text-[#008069] font-medium"
            >
              {btn.text || 'Reply'}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

/** WhatsApp-style template message preview (Meta Business Manager look). */
export default function WhatsAppTemplatePreview({
  headerType,
  headerText,
  headerTextExamples = [],
  headerMediaName,
  bodyText,
  bodyExamples = [],
  footerText,
  buttons = [],
  compact = false,
}) {
  const headerPreview = headerType === 'TEXT'
    ? fillPlaceholders(headerText, headerTextExamples)
    : headerText;
  const bodyPreview = fillPlaceholders(bodyText, bodyExamples);
  const ctaButtons = buttons.filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
  const quickReplies = buttons.filter((b) => b.type === 'QUICK_REPLY');

  return (
    <div className={`mx-auto ${compact ? 'max-w-[260px]' : 'max-w-[300px]'}`}>
      {/* Phone frame */}
      <div className="rounded-[28px] border-[6px] border-gray-800 bg-gray-800 shadow-xl overflow-hidden">
        <div className="bg-[#008069] px-4 py-2.5 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">R</div>
          <div>
            <p className="text-white text-sm font-medium leading-tight">RoadServe</p>
            <p className="text-white/70 text-[10px]">Business account</p>
          </div>
        </div>

        <div
          className="p-3 min-h-[220px]"
          style={{
            backgroundColor: '#efeae2',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d4cfc4\' fill-opacity=\'0.25\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}
        >
          <div className="bg-white rounded-lg shadow-sm overflow-hidden text-[13px] leading-relaxed">
            {headerType === 'TEXT' && headerPreview && (
              <p className="px-3 pt-3 font-semibold text-gray-900">{headerPreview}</p>
            )}
            {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType) && (
              <MediaHeaderPlaceholder type={headerType} label={headerMediaName || headerType} />
            )}
            <p className={`px-3 text-gray-800 whitespace-pre-wrap ${headerType && headerType !== 'NONE' ? 'pt-2 pb-1' : 'py-3'}`}>
              {bodyPreview || 'Message body…'}
            </p>
            {footerText && (
              <p className="px-3 pb-2.5 text-[11px] text-gray-400">{footerText}</p>
            )}
            {ctaButtons.map((btn, i) => (
              <PreviewButton key={`cta-${i}`} btn={btn} />
            ))}
          </div>

          {quickReplies.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2 justify-end">
              {quickReplies.map((btn, i) => (
                <span
                  key={`qr-${i}`}
                  className="text-[12px] bg-white border border-gray-200 rounded-full px-3 py-1.5 text-[#008069] shadow-sm font-medium"
                >
                  {btn.text || 'Reply'}
                </span>
              ))}
            </div>
          )}

          <p className="text-[10px] text-gray-500 text-center mt-3">Template preview</p>
        </div>
      </div>
    </div>
  );
}

export { fillPlaceholders };
